// Derives a nickname for the barber based on their unlocked achievements.
//
// Tier per category (count of unlocked thresholds in that category — each
// category has 8 thresholds split into 4 visible tiers of 2):
//   0     → none
//   1-2   → rookie  (T1 done — first wins)
//   3-4   → strong  (T2 done — habit forming)
//   5+    → elite   (T3+ entered — committed mastery)
//
// Pick rules — tightest match wins:
//   1. 5 elites      → ultra-rare ("The G.O.A.T.")
//   2. 4 elites      → quad master ("The Empire Builder")
//   3. 3 elites      → specific trio name
//   4. 2 elites      → specific duo name
//   5. 1 elite       → category-specific master
//   6. 0 elites, 1+ strong → "The Steady Hand"
//   7. 0 elites, 0 strong  → newbie tier on total unlocks
//                            (0 = Newcomer, 1-5 = Apprentice, 6-10 = Rising Star)
//
// Returns the i18n key + the list of categories that earned the title (for
// the subtitle pills).

import type { AchievementKind, AchievementRow } from '@busy/api';
import { ACHIEVEMENT_CATALOG } from './achievements';

export type Tier = 'none' | 'rookie' | 'strong' | 'elite';

// Nickname picks are anchored to the original 5 categories. Transformations is
// excluded here on purpose: the duo/trio/quad/goat tier strings are calibrated
// for 5 kinds, and bringing transformations into nickname picks would require
// a fresh combinatorial set of titles. Transformations still surface as a
// regular achievement category — they just don't drive the title.
const ALL_KINDS: AchievementKind[] = ['streak', 'clients', 'cuts', 'revenue', 'lessons'];

export function tierForCount(count: number): Tier {
  if (count === 0) return 'none';
  if (count <= 2) return 'rookie';
  if (count <= 4) return 'strong';
  return 'elite';
}

export type NicknameInfo = {
  /** i18n key resolved with t() — e.g. 'nickname.solo.clients'. */
  key: string;
  /** Categories that earned the nickname (for the subtitle badge row). */
  earnedFrom: AchievementKind[];
  /** Tiers per category — useful for the achievements page header summary. */
  tiers: Record<AchievementKind, Tier>;
  /** Total unlocked across all categories. */
  totalUnlocked: number;
};

export function deriveNickname(achievements: AchievementRow[]): NicknameInfo {
  // Count per kind
  const counts: Record<AchievementKind, number> = {
    streak: 0, clients: 0, cuts: 0, revenue: 0, lessons: 0, transformations: 0, timekeeper: 0,
  };
  for (const a of achievements) counts[a.kind]++;
  return deriveNicknameFromCounts(counts);
}

/**
 * Same nickname logic as `deriveNickname()` but takes the per-kind counts
 * directly. Used by leaderboard rendering, where the server returns
 * aggregated counts rather than the full achievement rows.
 */
export function deriveNicknameFromCounts(counts: Record<AchievementKind, number>): NicknameInfo {
  const tiers: Record<AchievementKind, Tier> = {
    streak: tierForCount(counts.streak),
    clients: tierForCount(counts.clients),
    cuts: tierForCount(counts.cuts),
    revenue: tierForCount(counts.revenue),
    lessons: tierForCount(counts.lessons),
    transformations: tierForCount(counts.transformations),
    timekeeper: tierForCount(counts.timekeeper),
  };

  const elite = ALL_KINDS.filter((k) => tiers[k] === 'elite');
  const strong = ALL_KINDS.filter((k) => tiers[k] === 'strong');
  const totalUnlocked =
    counts.streak +
    counts.clients +
    counts.cuts +
    counts.revenue +
    counts.lessons +
    counts.transformations +
    counts.timekeeper;

  // 5 elites — G.O.A.T.
  if (elite.length === 5) {
    return { key: 'nickname.goat', earnedFrom: elite, tiers, totalUnlocked };
  }
  // 4 elites — Empire Builder
  if (elite.length === 4) {
    return { key: 'nickname.empire', earnedFrom: elite, tiers, totalUnlocked };
  }
  // 3 elites — specific trio
  if (elite.length === 3) {
    const key = `nickname.trio.${elite.slice().sort().join('+')}`;
    return { key, earnedFrom: elite, tiers, totalUnlocked };
  }
  // 2 elites — specific duo
  if (elite.length === 2) {
    const key = `nickname.duo.${elite.slice().sort().join('+')}`;
    return { key, earnedFrom: elite, tiers, totalUnlocked };
  }
  // 1 elite — category-specific master
  if (elite.length === 1) {
    return { key: `nickname.solo.${elite[0]}`, earnedFrom: elite, tiers, totalUnlocked };
  }
  // No elites but at least one strong → Steady Hand
  if (strong.length > 0) {
    return { key: 'nickname.steady', earnedFrom: strong, tiers, totalUnlocked };
  }
  // Newbie tier — based on total unlocks. The branch only runs when no
  // category has reached "strong" (3+ unlocks), so the upper bound is
  // bounded mathematically: 5 categories × max 2 rookie unlocks = 10 unlocks
  // before *some* category must transition to strong and Steady Hand fires.
  //
  // Calibrated against the new 8-threshold structure where T1 awards a "first"
  // milestone (1 cut, 1 client, $100, etc.) so a barber on day one easily
  // accumulates several rookie unlocks across categories.
  if (totalUnlocked === 0) {
    return { key: 'nickname.newcomer', earnedFrom: [], tiers, totalUnlocked };
  }
  if (totalUnlocked <= 5) {
    // Day 1–first session: a few first milestones across categories.
    return { key: 'nickname.apprentice', earnedFrom: [], tiers, totalUnlocked };
  }
  // 6–10 unlocks with no category at strong yet — spread across most/all
  // categories at T1, working their way toward T2.
  return { key: 'nickname.rising', earnedFrom: [], tiers, totalUnlocked };
}

// Re-export catalog so callers can show progression hints if desired.
export { ACHIEVEMENT_CATALOG };

/** One entry in the barber's nickname history. */
export type NicknameJourneyEntry = {
  /** i18n key for the nickname title (e.g. 'nickname.solo.streak'). */
  key: string;
  /** ISO timestamp of the first achievement that qualified the user for this
   *  nickname. For 'nickname.newcomer' (the default before any achievement
   *  is earned) this is null — there's no event that "unlocks" being a
   *  newcomer; you start there. */
  firstEarnedAt: string | null;
  /** Categories that anchored this nickname when it was first reached. */
  earnedFrom: AchievementKind[];
};

/**
 * Walks the user's achievements in chronological order, derives the
 * nickname after each one, and returns every *distinct* nickname they
 * ever qualified for in the order it was first reached. Powers the
 * "Nickname Journey" section on the Achievements page.
 *
 * Pure — no side effects, no DB. Caller passes in the achievements array
 * (typically from useAchievements()).
 */
export function nicknameHistory(achievements: AchievementRow[]): NicknameJourneyEntry[] {
  // Always include 'newcomer' as the starting state — every barber begins
  // there before earning anything.
  const journey: NicknameJourneyEntry[] = [
    { key: 'nickname.newcomer', firstEarnedAt: null, earnedFrom: [] },
  ];
  const seen = new Set<string>(['nickname.newcomer']);

  // Sort ascending by earned_at so we can replay the timeline.
  const sorted = [...achievements].sort(
    (a, b) => new Date(a.earned_at).getTime() - new Date(b.earned_at).getTime(),
  );

  // Replay: after each achievement, derive the nickname from the running
  // counts so far. If it's new, log the entry with the achievement's
  // earned_at as the unlock moment.
  const counts: Record<AchievementKind, number> = {
    streak: 0, clients: 0, cuts: 0, revenue: 0, lessons: 0, transformations: 0, timekeeper: 0,
  };
  for (const a of sorted) {
    counts[a.kind]++;
    const nick = deriveNicknameFromCounts(counts);
    if (!seen.has(nick.key)) {
      seen.add(nick.key);
      journey.push({
        key: nick.key,
        firstEarnedAt: a.earned_at,
        earnedFrom: nick.earnedFrom,
      });
    }
  }
  return journey;
}
